import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import { Paragraph } from "@smartsuite/react-ui/lib";

import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormField } from "../../../../hooks/useFormField";
import { FormFieldProps } from "../../../types";
import { fieldsStateText } from "../../fields.text";
import { getLinkedRecordIds } from "../lookup.helpers";
import { LookupFormValue } from "../lookup.config";
import { useLookupValue } from "./hooks/useLookupValue";
import { $t } from "../../../../utils/intl";

import "./LookupFieldControl.sass";

export type LookupFieldControlProps = FormFieldProps;

export const LookupFieldControl: React.FunctionComponent<LookupFieldControlProps> = ({
  caption,
  field,
  formItem,
  label,
  name,
  required,
  context,
  isItemConditionSatisfied,
}) => {
  const { config } = context;
  const appStructure = config?.application?.structure ?? [];
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();

  const lrFieldSlug = field.params?.linked_field;
  const lrField = lrFieldSlug ? appStructure.find((f) => f.slug === lrFieldSlug) : undefined;
  const linkedApplication = lrField?.params?.linked_application;

  const lrValue = values[lrFieldSlug ?? ""];
  const selectedIds = getLinkedRecordIds(lrValue);

  const { displayValue, rawValue, isLoading } = useLookupValue({
    lookupField: field,
    lrFieldSlug: lrFieldSlug ?? "",
    linkedApplication: linkedApplication ?? "",
    selectedIds,
  });

  const formField = useFormField<LookupFormValue>({
    name,
    validate: () => undefined,
  });

  const rawValueJson = rawValue === null ? null : JSON.stringify(rawValue);

  useEffect(() => {
    if (!isLoading) {
      setFieldValue(name, rawValue ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, rawValueJson]);

  if (!isItemConditionSatisfied) return null;

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params?.help_text_as_tooltip}
      caption={caption}
      errorMessage={formField.errorMessage}
      label={label}
      required={required}
      helpText={formItem.params.helpText}
      state={formField.state}
      readOnly={true}
    >
      <div
        className="lookup-field-control__display"
        aria-readonly="true"
        data-testid="lookup-field-display"
      >
        {isLoading ? (
          <Paragraph color="inactive" size="s">
            {$t(fieldsStateText.loading)}
          </Paragraph>
        ) : displayValue ? (
          <Paragraph
            className="lookup-field-control__value"
            size="s"
            color="currentColor"
            wordBreak="break-word"
          >
            {displayValue}
          </Paragraph>
        ) : null}
      </div>
    </FormFieldControl>
  );
};
